import { cn } from '../../../lib/stylingUtils';

export type SkeletonProps = React.HTMLAttributes<HTMLDivElement> & {
  /** Additional CSS classes */
  className?: string;
  /** Width of the skeleton (CSS value or preset) */
  width?: string | number;
  /** Height of the skeleton (CSS value or preset) */
  height?: string | number;
  /** Variant for common skeleton types */
  variant?: 'text' | 'circular' | 'rectangular' | 'rounded';
};

/**
 * Skeleton component for loading placeholders
 *
 * Provides animated placeholder elements during loading states.
 * Supports multiple variants for common UI patterns.
 *
 * @example
 * ```tsx
 * // Text placeholder
 * <Skeleton variant="text" width="200px" />
 *
 * // Avatar placeholder
 * <Skeleton variant="circular" width={40} height={40} />
 *
 * // Card placeholder
 * <Skeleton variant="rounded" width="100%" height={120} />
 * ```
 */
export const Skeleton = ({
  className,
  width,
  height,
  variant = 'rectangular',
  ...props
}: SkeletonProps) => {
  const variantClasses = {
    text: 'rounded',
    circular: 'rounded-full',
    rectangular: 'rounded-none',
    rounded: 'rounded-md',
  };

  const style = {
    width: typeof width === 'number' ? `${width}px` : width,
    height: typeof height === 'number' ? `${height}px` : height,
  };

  return (
    <div
      className={cn(
        '@Skeleton',
        cn('animate-pulse bg-neutral/10', variantClasses[variant], className),
      )}
      style={style}
      {...props}
    />
  );
};

/**
 * SkeletonText - Skeleton for text lines
 *
 * @example
 * ```tsx
 * <SkeletonText lines={3} />
 * ```
 */
export type SkeletonTextProps = {
  lines?: number;
  className?: string;
};

export const SkeletonText = ({ lines = 1, className }: SkeletonTextProps) => (
  <div className={cn('@SkeletonText', cn('space-y-2', className))}>
    {Array.from({ length: lines }).map((_, i) => (
      <Skeleton
        key={i}
        variant="text"
        height={16}
        width={i === lines - 1 ? '80%' : '100%'}
      />
    ))}
  </div>
);

/**
 * SkeletonCard - Skeleton for card layouts
 *
 * @example
 * ```tsx
 * <SkeletonCard />
 * ```
 */
export type SkeletonCardProps = { className?: string };

export const SkeletonCard = ({ className }: SkeletonCardProps) => (
  <div
    className={cn(
      '@SkeletonCard',
      cn('space-y-3 rounded-lg border p-4', className),
    )}
  >
    <Skeleton variant="rounded" height={120} width="100%" />
    <SkeletonText lines={2} />
    <div className="flex gap-2">
      <Skeleton variant="rounded" height={32} width={80} />
      <Skeleton variant="rounded" height={32} width={80} />
    </div>
  </div>
);

/**
 * SkeletonAvatar - Skeleton for avatar/profile images
 *
 * @example
 * ```tsx
 * <SkeletonAvatar size={40} />
 * ```
 */
export type SkeletonAvatarProps = {
  size?: number;
  className?: string;
};

export const SkeletonAvatar = ({
  size = 40,
  className,
}: SkeletonAvatarProps) => (
  <Skeleton
    variant="circular"
    width={size}
    height={size}
    className={cn('@SkeletonAvatar', className)}
  />
);

/**
 * SkeletonTable - Skeleton for table rows
 *
 * @example
 * ```tsx
 * <SkeletonTable rows={5} columns={4} />
 * ```
 */
export type SkeletonTableProps = {
  rows?: number;
  columns?: number;
  className?: string;
};

export const SkeletonTable = ({
  rows = 3,
  columns = 3,
  className,
}: SkeletonTableProps) => (
  <div className={cn('@SkeletonTable', cn('space-y-2', className))}>
    {Array.from({ length: rows }).map((_, rowIndex) => (
      <div key={rowIndex} className="flex gap-4">
        {Array.from({ length: columns }).map((_, colIndex) => (
          <Skeleton key={colIndex} height={40} className="flex-1" />
        ))}
      </div>
    ))}
  </div>
);
